/**
 * 邮件签名编辑器组件 — 用于创建和编辑邮件账户的 HTML 签名。
 *
 * 主要导出：`CrmSignatureEditor`（自定义元素 `<crm-signature-editor>`）
 *
 * 功能特性：
 * - 支持富文本编辑（通过 `<crm-rich-editor>` 子组件）
 * - 支持签名内嵌图片上传（CID 引用 ↔ URL 显示互转）
 * - 自动加载当前账户的激活签名或第一个签名
 * - 创建模式：当无已存在签名时，先创建签名记录再上传图片
 *
 * 依赖关系：
 * - 继承 `BaseElement`
 * - 通过 `emailStore` 调用签名的 CRUD 接口
 * - 内部使用 `<crm-rich-editor>` 富文本编辑器
 * - 从 `api.ts` 获取 `API_BASE_URL` 用于拼接图片访问 URL
 *
 * 对外事件：
 * - `crm-signature-saved` — 签名保存成功后冒泡，携带签名 ID
 * - `crm-signature-cancel` — 用户点击取消按钮时冒泡
 */
import { css, html } from 'lit';
import { customElement, state, property, query } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';
import { emailStore } from '../store.js';
import { toast } from './toast.js';
import { API_BASE_URL } from '../api.js';
import type { CrmRichEditor } from './rich-editor.js';
import './rich-editor.js';

@customElement('crm-signature-editor')
export class CrmSignatureEditor extends BaseElement {
  /** 当前邮件账户 ID，用于加载和保存签名 */
  @property({ type: Number }) accountId = 0;

  /** 签名描述（显示名称） */
  @state() private description = '';
  /** 签名 HTML 内容（已将 CID 引用转为可访问 URL） */
  @state() private content = '';
  /** 当前编辑的签名 ID，null 表示新建模式 */
  @state() private signatureId: number | null = null;
  /** 数据加载中状态 */
  @state() private loading = true;
  /** 保存中状态，用于禁用按钮 */
  @state() private saving = false;

  /** 富文本编辑器实例引用（通过 @query 装饰器获取） */
  @query('crm-rich-editor') private richEditor!: CrmRichEditor;

  static styles = css`
    :host { display: block; }
  `;

  /**
   * 签名图片访问的 URL 前缀。
   * 格式：`{API_BASE_URL}/emails/signature-images/{accountId}/{signatureId}/`
   * 当 signatureId 为 null 时用 0 占位（此时仅用于预览，实际上传前会先创建签名）。
   */
  private get imageBaseUrl(): string {
    return `${API_BASE_URL}/emails/signature-images/${this.accountId}/${this.signatureId || 0}/`;
  }

  /**
   * 将签名 HTML 中的 `src="cid:xxx"` 引用替换为可直接访问的图片 URL。
   * 这样富文本编辑器才能正确显示签名内嵌图片。
   * @param html - 原始签名 HTML（包含 cid: 引用）
   * @returns 转换后的 HTML（包含可直接预览的完整 URL）
   */
  private resolveCidToUrl(html: string): string {
    if (!html) return '';
    return html.replace(/src="cid:([^"]+)"/g, (_, cid) =>
      `src="${this.imageBaseUrl}${cid}"`);
  }

  /**
   * 将签名 HTML 中的图片 URL 还原为 `src="cid:xxx"` 引用。
   * 保存前调用，确保后端存储的是 CID 格式而非带 token 的完整 URL。
   * 同时兼容清理旧版本遗留的 token 查询参数。
   * @param html - 编辑器输出的 HTML（包含预览 URL）
   * @returns 还原后的 HTML（包含 cid: 引用）
   */
  private restoreUrlToCid(html: string): string {
    // 转义 imageBaseUrl 中的正则特殊字符
    const base = this.imageBaseUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return html.replace(new RegExp(`src="${base}([^"?]+)(?:\\?token=[^"]*)?"`, 'g'),
      (_, cid) => `src="cid:${cid}"`);
  }

  /** 生命周期：组件连接到 DOM 时加载激活签名 */
  connectedCallback() {
    super.connectedCallback();
    this.loadActiveSignature();
  }

  /**
   * 生命周期：属性变化后触发。
   * 当 loading 从 true 变为 false 时（数据加载完成），延迟到下一帧将内容设置到富文本编辑器。
   * 使用 requestAnimationFrame 确保 DOM 已稳定且 @query 已解析。
   * @param changedProps - 发生变化的属性 Map
   */
  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('loading') && !this.loading) {
      // Defer to next frame so rich-editor DOM is settled and @query resolves
      requestAnimationFrame(() => {
        if (this.richEditor) {
          this.richEditor.value = this.content;
          this.richEditor.focusEditor();
        }
      });
    }
  }

  /**
   * 加载当前账户的激活签名（或回退到签名列表第一个）。
   * 加载完成后将 CID 引用转为 URL 以便在编辑器中正确显示图片。
   * @sideeffect 设置 signatureId、description、content 状态
   */
  private async loadActiveSignature() {
    this.loading = true;
    try {
      await emailStore.fetchSignatures(this.accountId);
      await emailStore.fetchActiveSignature(this.accountId);
      const active = emailStore.state.activeSignature[this.accountId];
      if (active && active.id) {
        // 找到激活签名，使用其内容
        this.signatureId = active.id;
        this.description = active.description;
        this.content = this.resolveCidToUrl(active.content);
      } else {
        // Fall back to first signature from the list — 无激活签名时回退到列表第一项
        const list = emailStore.state.signatures[this.accountId] || [];
        if (list.length > 0) {
          const sig = list[0];
          this.signatureId = sig.id;
          this.description = sig.description;
          this.content = this.resolveCidToUrl(sig.content);
        }
      }
    } catch {
      // no signature, start in create mode — 无签名记录，进入新建模式
    } finally {
      this.loading = false;
    }
  }

  /**
   * 签名图片上传处理函数（传给富文本编辑器的 customImageUploader）。
   * 如果当前尚未创建签名记录，先创建签名再上传图片。
   * @param file - 用户选择的图片文件
   * @returns 上传结果的 CID 和可访问的显示 URL
   */
  private uploadImage = async (file: File): Promise<{ cid: string; displayUrl: string }> => {
    if (!this.signatureId) {
      // 尚无签名记录，先创建（不关闭编辑器）
      await this.saveSignature(false);
    }
    if (!this.signatureId) throw new Error('Failed to create signature');
    const result = await emailStore.uploadSignatureImage(this.accountId, this.signatureId, file);
    return {
      cid: result.cid,
      displayUrl: `${this.imageBaseUrl}${result.cid}`,
    };
  };

  /**
   * 保存签名 — 新建或更新当前签名。
   * 保存前将编辑器内容中的 URL 还原为 CID 引用格式。
   * @param closeAfter - 保存成功后是否派发 `crm-signature-saved` 事件（关闭编辑器）
   * @sideeffect 调用后端 API 创建/更新签名，可能派发事件
   */
  private saveSignature = async (closeAfter = true) => {
    this.saving = true;
    try {
      // 获取编辑器内容并还原 CID 引用
      const content = this.richEditor?.value || this.content;
      const data = { description: this.description || '签名', content: this.restoreUrlToCid(content) };
      if (this.signatureId) {
        // 更新已存在签名
        await emailStore.updateSignature(this.accountId, this.signatureId, data);
      } else {
        // 新建签名
        const sig = await emailStore.createSignature(this.accountId, data);
        this.signatureId = sig.id;
      }
      if (closeAfter) {
        // 派发保存成功事件，携带签名 ID
        this.dispatchEvent(new CustomEvent('crm-signature-saved', {
          detail: this.signatureId, bubbles: true, composed: true,
        }));
      }
    } catch (err: any) {
      toast.error(err.message || '保存失败');
    } finally {
      this.saving = false;
    }
  };

  /**
   * 渲染签名编辑器。
   * 加载中显示加载提示，加载完成后渲染富文本编辑器和操作按钮（取消/保存）。
   * @returns Lit 模板
   */
  render() {
    if (this.loading) {
      return html`<div class="flex items-center justify-center py-12 text-text-muted text-[14px]">加载中...</div>`;
    }

    return html`
      <div class="font-sans">
        <crm-rich-editor
          name="signature-content"
          placeholder="输入签名内容..."
          .customImageUploader=${this.uploadImage}
          style="min-height:60vh;"
        ></crm-rich-editor>

        <div class="flex justify-end gap-2 mt-4">
          <button @click=${() => {
            this.dispatchEvent(new CustomEvent('crm-signature-cancel', { bubbles: true, composed: true }));
          }} class="px-4 py-1.5 border border-border rounded-md bg-surface text-[13px] cursor-pointer">取消</button>
          <button @click=${() => this.saveSignature()} ?disabled=${this.saving}
            class="px-4 py-1.5 border-none rounded bg-black text-white text-[13px] cursor-pointer">
            ${this.saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    `;
  }
}
